import { useQueryClient } from '@tanstack/react-query'
import { Archive, ArchiveRestore, ListTree } from 'lucide-react'
import { useState } from 'react'
import { Link } from 'react-router'

import { consulta, ErrorDeLaApi, pedir } from '../api/nucleo'
import { Aviso, Boton } from '../componentes/ui'
import { CABECERA_EDITADO_DESDE } from '../motor/conflicto'

/**
 * Las acciones propias del editor de un tipo de contenido o de una taxonomía (plan del CMS
 * dinámico §2.1 y decisión 2): un tipo con contenidos no se borra, **se archiva** —deja de verse
 * en la web y en el menú, y sus contenidos se quedan—; y desde el tipo se va a sus contenidos.
 *
 * Archivar guarda solo el estado, con la misma marca de versión que el editor (§18): si otra
 * persona guardó mientras tanto, el núcleo responde 409 y no se pisa nada. En un tipo con cambios
 * sin publicar, el archivado va en su borrador y se aplica al publicar.
 */
export const AccionesDeEstructura = ({
  coleccion,
  espacio,
  sitio,
  id,
  valores,
  sucio,
  recargar,
}: {
  coleccion: string
  espacio: number
  id: null | number
  recargar: () => void
  sitio: number
  sucio: boolean
  valores: Record<string, unknown>
}) => {
  const clienteDeConsultas = useQueryClient()
  const [ocupado, setOcupado] = useState(false)
  const [fallo, setFallo] = useState<null | string>(null)
  if (id === null || coleccion !== 'tipos-de-contenido') return null
  const archivado = valores.estado === 'archivado'
  const enBorrador = valores._status !== 'published'

  const cambiarEstado = async () => {
    const pregunta = archivado
      ? '¿Reactivar este tipo? Vuelve al menú y, publicado, a la web.'
      : '¿Archivar este tipo? Deja de verse en la web y en el menú del panel; sus contenidos no se borran y puedes reactivarlo cuando quieras.'
    if (!window.confirm(pregunta)) return
    setOcupado(true)
    setFallo(null)
    try {
      const marca = typeof valores.updatedAt === 'string' ? valores.updatedAt : undefined
      await pedir(`/${coleccion}/${id}${consulta({ depth: 0, draft: enBorrador ? true : undefined })}`, {
        metodo: 'PATCH',
        cuerpo: { estado: archivado ? 'activo' : 'archivado', _status: enBorrador ? 'draft' : 'published' },
        cabeceras: marca ? { [CABECERA_EDITADO_DESDE]: marca } : undefined,
      })
      void clienteDeConsultas.invalidateQueries({ queryKey: ['lista', coleccion] })
      recargar()
    } catch (e) {
      setFallo(e instanceof ErrorDeLaApi && e.status === 409 && e.datos.conflicto === true ? 'Alguien ha guardado este tipo mientras tanto. Recarga la página y vuelve a intentarlo.' : e instanceof Error ? e.message : 'No se ha podido cambiar. Vuelve a intentarlo.')
    } finally {
      setOcupado(false)
    }
  }

  return (
    <span className="inline-flex flex-wrap items-center gap-2">
      <Link to={`/e/${espacio}/s/${sitio}/contenidos/${id}`}>
        <Boton icono={<ListTree />} pequeno tipo="discreto">
          Sus contenidos
        </Boton>
      </Link>
      <Boton
        cargando={ocupado}
        disabled={sucio}
        icono={archivado ? <ArchiveRestore /> : <Archive />}
        onClick={() => void cambiarEstado()}
        pequeno
        title={sucio ? 'Guarda antes tus cambios.' : enBorrador ? 'Va en el borrador: se aplica al publicar.' : undefined}
      >
        {archivado ? 'Reactivar' : 'Archivar'}
      </Boton>
      {fallo && (
        <span className="text-xs text-error" role="alert">
          {fallo}
        </span>
      )}
    </span>
  )
}

/** A quien no tiene `plantillas.editar`: puede cambiar el tipo o la taxonomía, pero el núcleo no le guardará las plantillas. */
export const AvisoSinPlantillas = () => (
  <Aviso tono="info">Puedes cambiar sus datos, pero no sus plantillas: si tocas un bloque, no se podrá guardar. Pídeselo a quien administra la cuenta.</Aviso>
)
